<template>
  <section class="section">
    <div class="container">
      <Tips
        path="computerPackages"
        xKey="dismissedTips"
        property="onPackageChangeStatus"
      >
        Tip: Activate package notifications and status in the profile settings
        up to the right.
      </Tips>

      <div class="right">
        <ComputerGroups></ComputerGroups>
      </div>

      <Title>Packages </Title>

      <div class="margin-bottom-1">
        <CheckboxText
          css="is-inline-block margin-right-10"
          :checked="internalExternal"
          @click="
            tempFilter = '';
            internalExternal = true;
            internalExternal === true ? getLatestPackages() : false;
          "
          >Packages on this computer</CheckboxText
        >

        <CheckboxText
          css="is-inline-block"
          :checked="!internalExternal"
          @click="
            tempFilter = '';
            internalExternal = false;
            search('');
          "
          >All available packages</CheckboxText
        >

        <CheckboxText
          v-if="!internalExternal"
          css
          :checked="includePrerelease"
          @click="
            includePrerelease = !includePrerelease;
            focus(searchId);
          "
          >Include pre-release</CheckboxText
        >
      </div>

      <SearchBar
        v-model="tempFilter"
        :id="searchId"
        :selectOnSubmit="true"
        :loading="searching"
        :focus="true"
        @submit="search($event)"
      ></SearchBar>

      <LoaderOne v-show="firstLoad"></LoaderOne>

      <p v-show="!searching && visiblePackagesLength === 0">
        <i>No packages.</i>
      </p>

      <transition name="fade" appear>
        <table
          v-show="!searching && visiblePackagesLength !== 0"
          class="table is-hoverable is-narrow is-fullwidth"
        >
          <thead>
            <tr class="is-unselectable">
              <th class="is-hidden-touch">Icon</th>
              <th @click="dispatchOrder('chocoPackage.Title')">Title</th>
              <th @click="dispatchOrder('source.name')">
                Repo
              </th>

              <th @click="dispatchOrder('chocoPackage.IsLatestVersion')">
                Version
              </th>
              <th
                @click="dispatchOrder('chocoPackage.PackageScanStatus')"
                class="is-hidden-touch"
              >
                Health
              </th>
              <th @click="dispatchOrder('command.name')">Action</th>
              <th @click="dispatchOrder('command.status')">Status</th>

              <th
                class="is-hidden-touch"
                @click="dispatchOrder('command.execDone', true)"
              >
                Completed
              </th>
            </tr>
          </thead>
          <tbody>
            <template
              v-for="arr of filtered(
                $store.state.computerPackage.mergedPackages
              )"
            >
              <tr
                v-for="item in selected(arr)"
                :key="key(item)"
                :id="item.chocoPackage.Id"
              >
                <td class="is-hidden-touch">
                  <figure class="image is-24x24">
                    <img :src="icon(item)" alt />
                  </figure>
                </td>
                <td>
                  <a
                    class="has-text-link"
                    :data-tooltip="item.chocoPackage.Title"
                    @click="toggle(item, 'showInfo', true)"
                    >{{ title(item) }}</a
                  >
                </td>

                <td>
                  <router-link
                    v-if="item.source"
                    :to="`/repos/${item.source.uid}/`"
                  >
                    <span v-if="!isPseudo(item)">{{ item.source.name }}</span>
                    <span
                      v-else
                      data-tooltip="Package was not found in repo"
                      class="has-text-danger"
                    >
                      {{ item.source.name }}
                    </span>
                  </router-link>
                </td>

                <td>
                  <Versions
                    :items="arr"
                    :isGroup="false"
                    @change="jump($event)"
                  ></Versions>
                </td>
                <td class="is-hidden-touch">
                  <NotApproved :item="item" :showTooltip="true">
                    <i class="mdi mdi-alert-outline has-text-warning"></i>
                  </NotApproved>

                  <FailedValidations :item="item" :showTooltip="true">
                    <i class="mdi mdi-alert-outline has-text-warning"></i>
                  </FailedValidations>

                  <FailedTests :item="item" :showTooltip="true">
                    <i class="mdi mdi-alert-outline has-text-warning"></i>
                  </FailedTests>

                  <FailedVirusScan :item="item" :showTooltip="true">
                    <i class="mdi mdi-alert-outline has-text-warning"></i>
                  </FailedVirusScan>

                  <Passed :item="item" :showTooltip="false">
                    <i class="mdi mdi-check has-text-success"></i>
                  </Passed>
                </td>
                <td>
                  <InstallButton
                    :item="item"
                    @click="toggle(item, 'showActions', true)"
                  ></InstallButton>

                  <UninstallButton
                    :item="item"
                    @click="toggle(item, 'showActions', true)"
                  ></UninstallButton>
                </td>
                <td>
                  <div
                    v-if="item.command && item.command.name"
                    class="clickable tags has-addons"
                    @click="toggle(item, 'showActionInfo', true)"
                  >
                    <router-link
                      v-if="groupInfo(item)"
                      :to="
                        `/domains/${$route.params.domainUID}/groups/${
                          groupInfo(item).uid
                        }/packages`
                      "
                      :data-tooltip="`Group: ${groupInfo(item).name}`"
                      class="tag is-rounded is-info is-light"
                    >
                      <i class="mdi mdi-monitor-multiple"></i>
                    </router-link>

                    <span
                      v-if="groupInfo(item) && item.groupInfo.isAutoUpgrade"
                      class="tag is-rounded is-light is-info"
                      data-tooltip="Auto upgrade"
                    >
                      <i class="mdi mdi-cached"></i>
                    </span>

                    <span class="tag is-rounded is-light is-capitalized">{{
                      item.command.name
                    }}</span>

                    <span
                      v-if="hasParam(item, '-f')"
                      class="tag is-rounded is-light"
                      data-tooltip="Forced"
                      >-f</span
                    >

                    <span
                      v-if="hasParam(item, '-m')"
                      class="tag is-rounded is-light"
                      data-tooltip="Side by side installation"
                      >-m</span
                    >

                    <span
                      v-if="hasParam(item, '-n')"
                      class="tag is-rounded is-light"
                      data-tooltip="Add/remove package only"
                      >-n</span
                    >

                    <span
                      v-if="item.command.status === 'done'"
                      class="tag is-rounded is-success is-light"
                      data-tooltip="Successful"
                    >
                      <i class="mdi mdi-check"></i>
                    </span>

                    <span
                      v-if="item.command.status === 'failed'"
                      class="tag is-rounded is-danger is-light"
                      data-tooltip="Failed"
                    >
                      <i class="mdi mdi-information"></i>
                    </span>

                    <span
                      v-if="item.command.status === 'working'"
                      class="tag is-rounded is-info is-light"
                      data-tooltip="Working"
                    >
                      <i class="mdi mdi-timer-sand-empty"></i>
                    </span>

                    <span
                      v-if="item.command.status === 'pending'"
                      class="tag is-rounded is-primary is-light"
                      data-tooltip="Pending"
                    >
                      <i class="mdi mdi-clock-outline"></i>
                    </span>
                  </div>
                </td>

                <td v-if="item.command" class="is-hidden-touch">
                  {{ fromNow(item.command.execDone) }}
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </transition>

      <Paging
        v-if="visiblePackagesLength > 0 && !searching"
        :items="paging.pages"
        :selected="paging.page"
        @change="setPage($event)"
        :size="paging.max"
        @size="setPageMax($event)"
        :hasOptions="true"
        autosave="computerPackages"
      ></Paging>
    </div>

    <Info
      v-if="selectedItem && showInfo"
      v-model="showInfo"
      :item="selectedItem"
      @openActions="showActions = true"
      @hide="toggle(selectedItem, 'showInfo', false)"
    ></Info>

    <Actions
      v-if="selectedItem && showActions"
      v-model="showActions"
      :item="selectedItem"
      @hide="toggle(selectedItem, 'showActions', false)"
      @openActionInfo="
        toggle(null, 'showActions', false);
        toggle(null, 'showActionInfo', true);
      "
    ></Actions>

    <ActionInfo
      v-if="selectedItem && showActionInfo"
      v-model="showActionInfo"
      :itemRef="selectedItem"
      @openActions="
        toggle(null, 'showActionInfo', false);
        toggle(null, 'showActions', true);
      "
      @hide="toggle(selectedItem, 'showActionInfo', false)"
    ></ActionInfo>
  </section>
</template>

<style lang="css" scoped>
th {
  font-weight: normal;
}
</style>

<script>
import { fromNow } from "@/utils/date";
import { hasParam, getPackageNameVersionAndHash, getGroup } from "@/utils/mdc";
import { focus, getId, jump } from "@/utils/document";
import packageStatuses from "@/utils/packageStatus";
import { filterPackages } from "@/utils/packageFilter";
import { dispatchOrder } from "@/utils/packageOrder";
import odata from "@/utils/packageOdata";
import Actions from "@/components/main/packages/Actions.vue";
import Info from "@/components/main/packages/Info.vue";
import {
  getStartIndex,
  getPackageNumbers,
  getPackageObj,
} from "@/utils/paging";

export default {
  components: {
    PackagesEffect: () => import("@/components/singles/PackagesEffect.vue"),
    Tips: () => import("@/components/main/Tips.vue"),
    Paging: () => import("@/components/main/paging/Nav.vue"),
    Title: () => import("@/components/singles/Title.vue"),
    SearchBar: () => import("@/components/main/SearchBar.vue"),
    Versions: () => import("@/components/main/packages/Versions.vue"),
    ActionInfo: () => import("@/components/main/packages/ActionInfo.vue"),
    CheckboxText: () => import("@/components/singles/CheckboxText.vue"),
    LoaderOne: () => import("@/components/singles/LoaderOne.vue"),
    NotApproved: () =>
      import("@/components/main/packages/warnings/NotApproved.vue"),
    FailedValidations: () =>
      import("@/components/main/packages/warnings/FailedValidations.vue"),
    FailedTests: () =>
      import("@/components/main/packages/warnings/FailedTests.vue"),
    FailedVirusScan: () =>
      import("@/components/main/packages/warnings/FailedVirusScan.vue"),
    Passed: () => import("@/components/main/packages/warnings/Passed.vue"),
    InstallButton: () =>
      import("@/components/main/packages/buttons/Install.vue"),
    UninstallButton: () =>
      import("@/components/main/packages/buttons/Uninstall.vue"),
    ComputerGroups: () => import("@/components/main/group/ComputerGroups.vue"),
    Actions,
    Info,
  },

  computed: {
    key() {
      return (item) => {
        return (
          item.chocoPackage.Id + item.chocoPackage.Version + item.source?.uid
        );
      };
    },

    selected() {
      return (arr) => {
        return arr.filter((x) => x.selected);
      };
    },

    selectedItem() {
      return this.$store.getters["computerPackage/selectedItem"];
    },

    filtered() {
      return (packagesObj) => {
        if (this.filter && this.filter !== "") {
          const newPackagesObj = filterPackages(packagesObj, this.filter);
          this.visiblePackagesLength = Object.keys(newPackagesObj).length;

          return getPackageObj(
            newPackagesObj,
            this.paging.start,
            this.paging.page * this.paging.max
          );
        } else {
          this.visiblePackagesLength = Object.keys(packagesObj).length;

          return getPackageObj(
            packagesObj,
            this.paging.start,
            this.paging.page * this.paging.max
          );
        }
      };
    },
    groupInfo() {
      return (item) => {
        return getGroup(item);
      };
    },
    name() {
      return (item) => {
        return this.$helpers.getNameOrFriendlyName(item);
      };
    },
    icon() {
      return (item) => {
        return item.chocoPackage.IconUrl && item.chocoPackage.IconUrl !== ""
          ? item.chocoPackage.IconUrl
          : "/box.png";
      };
    },
    hasParam() {
      return (item, param) => {
        return hasParam(item.command.parameters, param);
      };
    },
    isPseudo() {
      return (item) => {
        return item?.chocoPackage?.Pseudo;
      };
    },
    nameAndVersion() {
      return (item) => {
        return getPackageNameVersionAndHash(item);
      };
    },
    title() {
      return (item) => {
        return item.chocoPackage.Title.length > 30
          ? `${item.chocoPackage.Title.substring(0, 30)}...`
          : item.chocoPackage.Title;
      };
    },
    isWorking() {
      return (item) => {
        return packageStatuses.isWorking(item);
      };
    },
    uninstallable() {
      return (item) => {
        return packageStatuses.isUninstallable(item);
      };
    },
    fromNow() {
      return (date) => {
        return fromNow(date);
      };
    },
  },
  data() {
    return {
      groups: null,
      visiblePackagesLength: 0,
      firstLoad: false,
      searching: false,
      internalExternal: true,
      showActions: false,
      showInfo: false,
      showActionInfo: false,
      tempFilter: "",
      filter: "",
      includePrerelease: false,
      searchId: getId(),

      paging: {
        page: 1,
        max: 15,
        start: 0,
        pages: [],
      },
    };
  },
  async created() {
    try {
      this.firstLoad = true;

      await this.$store.dispatch("group/fetch", {
        domainUID: this.$route.params.domainUID,
      });

      await this.$store.dispatch("settings/getUserSetting", {
        path: "computerPackages",
        key: "paging",
      });

      const paging = this.$store.state.settings.user.computerPackages?.paging;
      if (paging && paging.max) this.paging.max = paging.max;

      await this.getLatestPackages();
      this.firstLoad = false;
    } catch (err) {
      console.log(err);
      this.$helpers.notify("Error", "Could not get data.", "danger", 5000);
    }
  },
  beforeDestroy() {
    if (this.selectedItem) {
      this.$store.commit("computerPackage/editMergedPackage", {
        p: this.selectedItem,
        obj: { selectedItem: false },
      });
    }

    this.$store.commit("computerPackage/clear");
  },
  methods: {
    jump(item) {
      jump(item.chocoPackage.Id);
    },
    setPageMax(size) {
      this.paging.max = size;

      this.setPage(1);
      this.setPages(
        Object.keys(this.$store.state.computerPackage.mergedPackages).length
      );
    },
    setPages(num) {
      this.paging.pages = getPackageNumbers(num, this.paging.max);
    },
    setPage(page) {
      this.paging.start = getStartIndex(page, this.paging.max);
      this.paging.page = page;
    },
    dispatchOrder: dispatchOrder,
    focus: focus,
    async getLatestPackages() {
      this.searching = true;

      await this.$store.dispatch("computerPackage/fetchAndMerge", {
        domainUID: this.$route.params.domainUID,
        computerUIDs: [this.$route.params.computerUID],
      });

      this.setPage(1);
      this.setPages(
        Object.keys(this.$store.state.computerPackage.mergedPackages).length
      );

      this.searching = false;
      focus(this.searchId);
    },
    async search(searchStr) {
      this.searching = true;

      if (!this.internalExternal) {
        this.filter = "";

        let encodedFilter = "";
        if (searchStr !== "") {
          encodedFilter = odata.getSearchString(
            searchStr,
            this.includePrerelease,
            null,
            this.paging.max
          );
        } else {
          encodedFilter = odata.getLatestPackagesString(
            this.paging.max,
            this.includePrerelease
          );
        }

        const filters = this.$store.state.source.sources.map((x) => {
          return { source: x, filter: encodedFilter };
        });

        await this.$store.dispatch("computerPackage/searchFetchAndMerge", {
          domainUID: this.$route.params.domainUID,
          computerUIDs: [this.$route.params.computerUID],
          filters: filters,
        });

        this.setPage(1);
        this.setPages(
          Object.keys(this.$store.state.computerPackage.mergedPackages).length
        );
      } else {
        this.setPage(1);
        this.filter = searchStr;
      }

      this.searching = false;
      focus(this.searchId);
    },

    toggle(item, what, showHide) {
      if (item && item.command) {
        this.$store.commit("computerPackage/selectItem", item);
      }

      this[what] = showHide;
    },
  },
};
</script>
